import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  AlertTriangle,
  Banknote,
  Calculator,
  ChevronDown,
  CheckCircle2,
  Download,
  History,
  Search,
  Users,
} from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { EmptyState } from '@/components/common/EmptyState'
import { Button } from '@/components/ui/Button'
import { MonthYearPicker } from './MonthYearPicker'
import { useCompanies } from '@/features/companies/hooks/useCompanies'
import { useUsersList } from '@/features/users/hooks/useUsers'
import {
  useCalculatePayrollPreview,
  usePayPayrollSelection,
} from '@/features/payroll/hooks/usePayrollLive'
import type {
  PayrollPreviewCompany,
  PayrollPreviewEmployee,
  PayrollSelectionPreview,
} from '@/features/payroll/types/payroll.types'
import { getCompanyColor } from '@/features/shifts/utils/companyColor'

const PAYROLL_FORECAST_STATE_KEY = 'payroll:forecast:workspace'

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

function periodLabel(year: number, month: number): string {
  return `${MONTH_NAMES[month - 1]} ${year}`
}

function asOfForMonth(year: number, month: number): string {
  const now = new Date()
  if (now.getUTCFullYear() === year && now.getUTCMonth() + 1 === month) {
    return now.toISOString()
  }
  return new Date(Date.UTC(year, month, 0, 23, 59, 59)).toISOString()
}

function fmt(value: number | undefined | null): string {
  return (value ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function employeeKey(employee: PayrollPreviewEmployee): string {
  return `${employee.company.id}:${employee.employee.id}`
}

function csvCell(value: string | number | undefined): string {
  const text = String(value ?? '')
  if (!/[",\n]/.test(text)) return text
  return `"${text.replaceAll('"', '""')}"`
}

type CalculationMode = 'company' | 'person'

interface PersonBreakdownRow {
  employee: { id: string; name?: string }
  companyCount: number
  readyCount: number
  pendingCount: number
  gross: number
  deductions: number
  tax: number
  netPay: number
  rows: PayrollPreviewEmployee[]
}

interface EmployeePreviewGroup {
  employee: { id: string; name?: string }
  rows: PayrollPreviewEmployee[]
  companyNames: string[]
  readyRows: PayrollPreviewEmployee[]
  status: 'ready' | 'blocked' | 'paid'
  currency?: string
  regularHours: number
  overtimeHours: number
  gross: number
  deductions: number
  tax: number
  netPay: number
  warnings: string[]
}

interface MultiSelectOption {
  id: string
  label: string
  description?: string
  disabled?: boolean
  color?: string
}

interface PayrollForecastStoredState {
  year: number
  month: number
  mode: CalculationMode
  selectedCompanyIds: string[]
  selectedUserIds: string[]
  selectedEmployeeKeys: string[]
  employeeSearch: string
  statusFilter: 'all' | 'ready' | 'blocked' | 'paid'
  companyFilter: string
  preview?: PayrollSelectionPreview
}

function readPayrollForecastState(): PayrollForecastStoredState | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(PAYROLL_FORECAST_STATE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<PayrollForecastStoredState>
    if (parsed.mode !== 'company' && parsed.mode !== 'person') return null
    if (!Number.isFinite(parsed.year) || !Number.isFinite(parsed.month)) return null
    return {
      year: Number(parsed.year),
      month: Number(parsed.month),
      mode: parsed.mode,
      selectedCompanyIds: parsed.selectedCompanyIds ?? [],
      selectedUserIds: parsed.selectedUserIds ?? [],
      selectedEmployeeKeys: parsed.selectedEmployeeKeys ?? [],
      employeeSearch: parsed.employeeSearch ?? '',
      statusFilter: parsed.statusFilter ?? 'all',
      companyFilter: parsed.companyFilter ?? 'all',
      preview: parsed.preview,
    }
  } catch {
    return null
  }
}

function writePayrollForecastState(state: PayrollForecastStoredState): void {
  if (typeof window === 'undefined') return
  window.sessionStorage.setItem(PAYROLL_FORECAST_STATE_KEY, JSON.stringify(state))
}

function companyStatusLabel(status: PayrollPreviewCompany['status']): string {
  switch (status) {
    case 'ready':
      return 'Ready'
    case 'partial':
      return 'Partial'
    case 'blocked':
      return 'Blocked'
    case 'paid':
      return 'Paid'
    default:
      return status
  }
}

function statusTone(status: PayrollPreviewCompany['status']): string {
  switch (status) {
    case 'ready':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
    case 'partial':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
    case 'blocked':
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
    case 'paid':
      return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
  }
}

function SummaryMetric({
  label,
  value,
  tone = 'text-gray-900 dark:text-gray-100',
}: {
  label: string
  value: string | number
  tone?: string
}): React.ReactElement {
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-3 dark:border-gray-700 dark:bg-gray-900/40">
      <div className={`text-xl font-semibold tabular-nums ${tone}`}>{value}</div>
      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{label}</div>
    </div>
  )
}

function PayrollEntityMultiSelect({
  label,
  placeholder,
  options,
  selectedIds,
  loading,
  onChange,
  showLabel = true,
}: {
  label: string
  placeholder: string
  options: MultiSelectOption[]
  selectedIds: string[]
  loading: boolean
  onChange: (ids: string[]) => void
  /** Show the field label above the control. Off in the compact toolbar row. */
  showLabel?: boolean
}): React.ReactElement {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const rootRef = useRef<HTMLDivElement | null>(null)
  const selected = options.filter((option) => selectedIds.includes(option.id))
  const selectedSummary =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? selected[0].label
        : `${selected.length} selected`
  const filtered = options.filter((option) => {
    const q = query.trim().toLowerCase()
    if (!q) return true
    return `${option.label} ${option.description ?? ''}`.toLowerCase().includes(q)
  })
  const enabledIds = options.filter((option) => !option.disabled).map((option) => option.id)

  const toggle = (id: string): void => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((selectedId) => selectedId !== id))
    } else {
      onChange([...selectedIds, id])
    }
  }

  useEffect(() => {
    if (!open) return

    const closeOnOutside = (event: PointerEvent): void => {
      const target = event.target
      if (!(target instanceof Node)) return
      if (!rootRef.current?.contains(target)) {
        setOpen(false)
      }
    }

    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false)
      }
    }

    document.addEventListener('pointerdown', closeOnOutside)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutside)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [open])

  return (
    <div ref={rootRef} className="relative min-w-0 flex-1">
      {showLabel && (
        <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
          {label}
        </label>
      )}
      <button
        type="button"
        aria-label={label}
        className="flex h-11 w-full items-center justify-between gap-2 rounded-xl border border-gray-200 bg-white/95 px-3.5 text-left text-sm text-gray-900 shadow-[0_1px_2px_rgba(15,23,42,0.05)] outline-none transition-colors hover:border-gray-300 focus:border-primary/40 focus:ring-4 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-800/90 dark:text-gray-100 dark:hover:border-gray-600"
        onClick={() => setOpen((value) => !value)}
      >
        <span className="flex min-w-0 items-center gap-2">
          {selected.length === 1 && selected[0].color ? (
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: selected[0].color }}
              aria-hidden
            />
          ) : null}
          <span className="min-w-0 truncate">{selectedSummary}</span>
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full z-30 mt-2 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
          <div className="border-b border-gray-100 p-3 dark:border-gray-700">
            <label className="relative block">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search"
                className="h-9 w-full rounded-md border border-gray-300 bg-white pl-9 pr-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
              />
            </label>
            <div className="mt-2 flex gap-2">
              <Button size="sm" variant="secondary" onClick={() => onChange(enabledIds)}>
                Select all
              </Button>
              <Button size="sm" variant="secondary" onClick={() => onChange([])}>
                Clear
              </Button>
            </div>
            {selected.length > 0 && (
              <div className="mt-3 flex max-h-16 flex-wrap gap-1 overflow-y-auto">
                {selected.map((option) => (
                  <span
                    key={option.id}
                    className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                  >
                    {option.color ? (
                      <span
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: option.color }}
                        aria-hidden
                      />
                    ) : null}
                    {option.label}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {loading ? (
              <div className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                Loading...
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                No matches
              </div>
            ) : (
              filtered.map((option) => {
                const checked = selectedIds.includes(option.id)
                return (
                  <label
                    key={option.id}
                    className={`flex cursor-pointer items-start gap-3 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700/60 ${
                      option.disabled ? 'cursor-not-allowed opacity-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={checked}
                      disabled={option.disabled}
                      onChange={() => toggle(option.id)}
                    />
                    {option.color ? (
                      <span
                        className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: option.color }}
                        aria-hidden
                      />
                    ) : null}
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-gray-900 dark:text-gray-100">
                        {option.label}
                      </span>
                      {option.description && (
                        <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                          {option.description}
                        </span>
                      )}
                    </span>
                  </label>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function CompanyBreakdown({
  companies,
}: {
  companies: PayrollPreviewCompany[]
}): React.ReactElement {
  return (
    <section className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Company Audit
        </h2>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Company cost, ready employees, and employees pending before payment.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
              <th className="px-4 py-3">Company</th>
              <th className="px-4 py-3 text-right">Employees</th>
              <th className="px-4 py-3 text-right">Ready</th>
              <th className="px-4 py-3 text-right">Pending</th>
              <th className="px-4 py-3 text-right">Gross</th>
              <th className="px-4 py-3 text-right">Net Payable</th>
              <th className="px-4 py-3 text-right">Status</th>
            </tr>
          </thead>
          <tbody>
            {companies.map((company) => (
              <tr
                key={company.company.id}
                className="border-b border-gray-100 last:border-0 dark:border-gray-700/70"
              >
                <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                  {company.company.name}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{company.employeeCount}</td>
                <td className="px-4 py-3 text-right tabular-nums text-green-700 dark:text-green-400">
                  {company.readyCount}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-amber-700 dark:text-amber-400">
                  {company.pendingCount}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {company.currency} {fmt(company.gross)}
                </td>
                <td className="px-4 py-3 text-right font-medium tabular-nums">
                  {company.currency} {fmt(company.netPay)}
                </td>
                <td className="px-4 py-3 text-right">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusTone(company.status)}`}>
                    {companyStatusLabel(company.status)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function PersonBreakdown({
  people,
}: {
  people: PersonBreakdownRow[]
}): React.ReactElement {
  return (
    <section className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Person Audit
        </h2>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Combined salary preview per person across all selected companies.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
              <th className="px-4 py-3">Person</th>
              <th className="px-4 py-3 text-right">Companies</th>
              <th className="px-4 py-3 text-right">Ready</th>
              <th className="px-4 py-3 text-right">Pending</th>
              <th className="px-4 py-3 text-right">Gross</th>
              <th className="px-4 py-3 text-right">Tax</th>
              <th className="px-4 py-3 text-right">Net Payable</th>
              <th className="px-4 py-3">Companies</th>
            </tr>
          </thead>
          <tbody>
            {people.map((person) => (
              <tr
                key={person.employee.id}
                className="border-b border-gray-100 last:border-0 dark:border-gray-700/70"
              >
                <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                  {person.employee.name ?? person.employee.id}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{person.companyCount}</td>
                <td className="px-4 py-3 text-right tabular-nums text-green-700 dark:text-green-400">
                  {person.readyCount}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-amber-700 dark:text-amber-400">
                  {person.pendingCount}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{fmt(person.gross)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{fmt(person.tax)}</td>
                <td className="px-4 py-3 text-right font-medium tabular-nums">
                  {fmt(person.netPay)}
                </td>
                <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                  {person.rows.map((row) => row.company.name).join(', ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function EmployeeRows({
  groups,
  selectedEmployeeKeys,
  onToggleGroup,
  onOpenGroup,
}: {
  groups: EmployeePreviewGroup[]
  selectedEmployeeKeys: Set<string>
  onToggleGroup: (group: EmployeePreviewGroup) => void
  onOpenGroup: (group: EmployeePreviewGroup) => void
}): React.ReactElement {
  return (
    <section className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Employee Salary Preview
        </h2>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Runtime calculation only. Payment will require an official saved snapshot.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
              <th className="px-4 py-3">Pay</th>
              <th className="px-4 py-3">Employee</th>
              <th className="px-4 py-3">Companies</th>
              <th className="px-4 py-3 text-right">Hours</th>
              <th className="px-4 py-3 text-right">OT</th>
              <th className="px-4 py-3 text-right">Gross</th>
              <th className="px-4 py-3 text-right">Deductions</th>
              <th className="px-4 py-3 text-right">Tax</th>
              <th className="px-4 py-3 text-right">Net</th>
              <th className="px-4 py-3 text-right">Status</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => {
              const allReadySelected =
                group.readyRows.length > 0 &&
                group.readyRows.every((employee) =>
                  selectedEmployeeKeys.has(employeeKey(employee)),
                )
              return (
              <tr
                key={group.employee.id}
                className="cursor-pointer border-b border-gray-100 transition-colors last:border-0 hover:bg-primary/5 focus:outline-none focus-visible:bg-primary/5 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/30 dark:border-gray-700/70 dark:hover:bg-primary/10"
                tabIndex={0}
                onClick={() => onOpenGroup(group)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    onOpenGroup(group)
                  }
                }}
              >
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={allReadySelected}
                    disabled={group.readyRows.length === 0}
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => onToggleGroup(group)}
                    aria-label={`Select ${group.employee.name ?? group.employee.id}`}
                  />
                </td>
                <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                  {group.employee.name ?? group.employee.id}
                  {group.warnings.length > 0 && (
                    <div className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                      {group.warnings.join(', ')}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                  {group.companyNames.join(', ')}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{group.regularHours}</td>
                <td className="px-4 py-3 text-right tabular-nums">{group.overtimeHours}</td>
                <td className="px-4 py-3 text-right tabular-nums">{fmt(group.gross)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{fmt(group.deductions)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{fmt(group.tax)}</td>
                <td className="px-4 py-3 text-right font-medium tabular-nums">
                  {group.currency ? `${group.currency} ` : ''}
                  {fmt(group.netPay)}
                </td>
                <td className="px-4 py-3 text-right">
                  {group.status === 'ready' ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                      <CheckCircle2 className="h-3 w-3" /> Ready
                    </span>
                  ) : group.status === 'paid' ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                      <CheckCircle2 className="h-3 w-3" /> Paid
                    </span>
                  ) : (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                      Pending
                    </span>
                  )}
                </td>
              </tr>
            )})}
          </tbody>
        </table>
      </div>
    </section>
  )
}

export function PayrollForecastPage(): React.ReactElement {
  const navigate = useNavigate()
  const now = new Date()
  const restoredState = useMemo(() => readPayrollForecastState(), [])
  const [year, setYear] = useState(restoredState?.year ?? now.getUTCFullYear())
  const [month, setMonth] = useState(restoredState?.month ?? now.getUTCMonth() + 1)
  const [mode, setMode] = useState<CalculationMode>(restoredState?.mode ?? 'company')
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>(
    restoredState?.selectedCompanyIds ?? [],
  )
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>(
    restoredState?.selectedUserIds ?? [],
  )
  const [selectedEmployeeKeys, setSelectedEmployeeKeys] = useState<Set<string>>(
    new Set(restoredState?.selectedEmployeeKeys ?? []),
  )
  const [employeeSearch, setEmployeeSearch] = useState(restoredState?.employeeSearch ?? '')
  const [statusFilter, setStatusFilter] = useState<'all' | 'ready' | 'blocked' | 'paid'>(
    restoredState?.statusFilter ?? 'all',
  )
  const [companyFilter, setCompanyFilter] = useState(restoredState?.companyFilter ?? 'all')
  const [persistedPreview, setPersistedPreview] = useState<PayrollSelectionPreview | undefined>(
    restoredState?.preview,
  )
  const { data: companies = [], isLoading: companiesLoading } = useCompanies()
  const { data: usersData, isLoading: usersLoading } = useUsersList(
    { status: 'active', limit: 100 },
    { enabled: mode === 'person' },
  )
  const calculatePreview = useCalculatePayrollPreview()
  const payPayrollSelection = usePayPayrollSelection()
  const preview = calculatePreview.data ?? persistedPreview
  const users = usersData?.data ?? []

  const selectedCompanies = useMemo(
    () => companies.filter((company) => selectedCompanyIds.includes(company._id)),
    [companies, selectedCompanyIds],
  )
  const selectedUsers = useMemo(
    () => users.filter((user) => selectedUserIds.includes(user._id)),
    [selectedUserIds, users],
  )
  const companyOptions = useMemo<MultiSelectOption[]>(
    () =>
      companies.map((company) => ({
        id: company._id,
        label: company.name,
        color: company.color ?? getCompanyColor(company._id).dot,
        description:
          typeof company.memberCount === 'number'
            ? `${company.memberCount} members`
            : undefined,
      })),
    [companies],
  )
  const userOptions = useMemo<MultiSelectOption[]>(
    () =>
      users.map((user) => {
        const memberships = user.companies?.length
          ? user.companies
          : user.company
            ? [user.company]
            : []
        return {
          id: user._id,
          label: user.name || `${user.firstName} ${user.lastName}`.trim() || user.email,
          description:
            memberships.length > 0
              ? memberships.map((company) => company.name).join(', ')
              : 'No active company',
          disabled: memberships.length === 0,
        }
      }),
    [users],
  )
  const companyIdsForSelectedUsers = useMemo(() => {
    const ids = new Set<string>()
    selectedUsers.forEach((user) => {
      const userCompanies = user.companies?.length ? user.companies : user.company ? [user.company] : []
      userCompanies.forEach((company) => ids.add(company.id))
    })
    return [...ids]
  }, [selectedUsers])
  const employeeRows = useMemo(
    () => preview?.companies.flatMap((company) => company.employees) ?? [],
    [preview],
  )
  const personRows = useMemo<PersonBreakdownRow[]>(() => {
    const byEmployee = new Map<string, PersonBreakdownRow>()
    employeeRows.forEach((row) => {
      const current = byEmployee.get(row.employee.id) ?? {
        employee: row.employee,
        companyCount: 0,
        readyCount: 0,
        pendingCount: 0,
        gross: 0,
        deductions: 0,
        tax: 0,
        netPay: 0,
        rows: [],
      }
      current.companyCount += 1
      current.readyCount += row.status === 'ready' ? 1 : 0
      current.pendingCount += row.status === 'blocked' ? 1 : 0
      current.gross += row.gross
      current.deductions += row.deductions
      current.tax += row.tax
      current.netPay += row.netPay
      current.rows.push(row)
      byEmployee.set(row.employee.id, current)
    })
    return [...byEmployee.values()]
  }, [employeeRows])
  const filteredEmployeeRows = useMemo(() => {
    const q = employeeSearch.trim().toLowerCase()
    return employeeRows.filter((employee) => {
      if (statusFilter !== 'all' && employee.status !== statusFilter) return false
      if (companyFilter !== 'all' && employee.company.id !== companyFilter) return false
      if (!q) return true
      return `${employee.employee.name ?? ''} ${employee.employee.id} ${employee.company.name}`
        .toLowerCase()
        .includes(q)
    })
  }, [companyFilter, employeeRows, employeeSearch, statusFilter])
  const filteredEmployeeGroups = useMemo<EmployeePreviewGroup[]>(() => {
    const byEmployee = new Map<string, EmployeePreviewGroup>()
    filteredEmployeeRows.forEach((row) => {
      const current = byEmployee.get(row.employee.id) ?? {
        employee: row.employee,
        rows: [],
        companyNames: [],
        readyRows: [],
        status: 'blocked' as const,
        currency: row.currency,
        regularHours: 0,
        overtimeHours: 0,
        gross: 0,
        deductions: 0,
        tax: 0,
        netPay: 0,
        warnings: [],
      }
      current.rows.push(row)
      if (!current.companyNames.includes(row.company.name)) {
        current.companyNames.push(row.company.name)
      }
      if (row.status === 'ready') current.readyRows.push(row)
      if (current.currency !== row.currency) current.currency = undefined
      current.regularHours += row.regularHours
      current.overtimeHours += row.overtimeHours
      current.gross += row.gross
      current.deductions += row.deductions
      current.tax += row.tax
      current.netPay += row.netPay
      if (row.warning && !current.warnings.includes(row.warning)) {
        current.warnings.push(row.warning)
      }
      byEmployee.set(row.employee.id, current)
    })

    return [...byEmployee.values()].map((group) => {
      const paidCount = group.rows.filter((row) => row.status === 'paid').length
      const blockedCount = group.rows.filter((row) => row.status === 'blocked').length
      return {
        ...group,
        regularHours: Math.round(group.regularHours * 100) / 100,
        overtimeHours: Math.round(group.overtimeHours * 100) / 100,
        gross: Math.round(group.gross * 100) / 100,
        deductions: Math.round(group.deductions * 100) / 100,
        tax: Math.round(group.tax * 100) / 100,
        netPay: Math.round(group.netPay * 100) / 100,
        status:
          group.readyRows.length > 0
            ? 'ready'
            : paidCount === group.rows.length
              ? 'paid'
              : blockedCount > 0
                ? 'blocked'
                : 'blocked',
      }
    })
  }, [filteredEmployeeRows])
  const selectedEmployeeRows = useMemo(
    () =>
      employeeRows.filter(
        (employee) =>
          employee.status === 'ready' && selectedEmployeeKeys.has(employeeKey(employee)),
      ),
    [employeeRows, selectedEmployeeKeys],
  )
  const selectedNetPay = useMemo(
    () => selectedEmployeeRows.reduce((sum, employee) => sum + employee.netPay, 0),
    [selectedEmployeeRows],
  )
  const showBreakdown =
    preview !== undefined && (preview.companies.length > 1 || personRows.length > 1)
  const showEmployeeFilters =
    preview !== undefined && (employeeRows.length > 5 || preview.companies.length > 1)

  useEffect(() => {
    if (!calculatePreview.data) return
    setPersistedPreview(calculatePreview.data)
    setSelectedEmployeeKeys(
      new Set(
        calculatePreview.data.companies
          .flatMap((company) => company.employees)
          .filter((employee) => employee.status === 'ready')
          .map(employeeKey),
      ),
    )
    setCompanyFilter('all')
    setStatusFilter('all')
    setEmployeeSearch('')
  }, [calculatePreview.data])

  useEffect(() => {
    writePayrollForecastState({
      year,
      month,
      mode,
      selectedCompanyIds,
      selectedUserIds,
      selectedEmployeeKeys: [...selectedEmployeeKeys],
      employeeSearch,
      statusFilter,
      companyFilter,
      preview,
    })
  }, [
    companyFilter,
    employeeSearch,
    mode,
    month,
    preview,
    selectedCompanyIds,
    selectedEmployeeKeys,
    selectedUserIds,
    statusFilter,
    year,
  ])

  const calculate = (): void => {
    const companyIds = mode === 'person' ? companyIdsForSelectedUsers : selectedCompanyIds
    const employeeIds = mode === 'person' ? selectedUserIds : undefined
    setSelectedEmployeeKeys(new Set())
    setPersistedPreview(undefined)
    calculatePreview.mutate({
      companyIds,
      employeeIds,
      asOf: asOfForMonth(year, month),
    })
  }

  const refreshPreview = (): void => {
    const companyIds = mode === 'person' ? companyIdsForSelectedUsers : selectedCompanyIds
    const employeeIds = mode === 'person' ? selectedUserIds : undefined
    calculatePreview.mutate({
      companyIds,
      employeeIds,
      asOf: asOfForMonth(year, month),
    })
  }

  const switchMode = (nextMode: CalculationMode): void => {
    setMode(nextMode)
    calculatePreview.reset()
    setPersistedPreview(undefined)
    setSelectedEmployeeKeys(new Set())
    setEmployeeSearch('')
    setCompanyFilter('all')
    setStatusFilter('all')
  }

  const toggleEmployeeGroup = (group: EmployeePreviewGroup): void => {
    if (group.readyRows.length === 0) return
    setSelectedEmployeeKeys((current) => {
      const next = new Set(current)
      const allSelected = group.readyRows.every((employee) =>
        next.has(employeeKey(employee)),
      )
      group.readyRows.forEach((employee) => {
        const key = employeeKey(employee)
        if (allSelected) next.delete(key)
        else next.add(key)
      })
      return next
    })
  }

  const openEmployeeGroup = (group: EmployeePreviewGroup): void => {
    navigate(`/payroll/forecast/items/all/${group.employee.id}`)
  }

  const selectFilteredReady = (): void => {
    setSelectedEmployeeKeys((current) => {
      const next = new Set(current)
      filteredEmployeeRows
        .filter((employee) => employee.status === 'ready')
        .forEach((employee) => next.add(employeeKey(employee)))
      return next
    })
  }

  const clearFiltered = (): void => {
    setSelectedEmployeeKeys((current) => {
      const next = new Set(current)
      filteredEmployeeRows.forEach((employee) => next.delete(employeeKey(employee)))
      return next
    })
  }

  const exportCsv = (): void => {
    const rows = selectedEmployeeRows.length > 0 ? selectedEmployeeRows : filteredEmployeeRows
    const header = [
      'Employee',
      'Employee ID',
      'Company',
      'Status',
      'Hours',
      'Overtime',
      'Gross',
      'Deductions',
      'Tax',
      'Net Pay',
      'Currency',
      'Warning',
    ]
    const csv = [
      header.map(csvCell).join(','),
      ...rows.map((employee) =>
        [
          employee.employee.name,
          employee.employee.id,
          employee.company.name,
          employee.status,
          employee.regularHours,
          employee.overtimeHours,
          employee.gross,
          employee.deductions,
          employee.tax,
          employee.netPay,
          employee.currency,
          employee.warning,
        ]
          .map(csvCell)
          .join(','),
      ),
    ].join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
    const link = document.createElement('a')
    link.href = url
    link.download = `payroll-preview-${year}-${String(month).padStart(2, '0')}.csv`
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }

  const paySelected = async (): Promise<void> => {
    if (selectedEmployeeRows.length === 0 || payPayrollSelection.isPending) return
    const companyIds = [...new Set(selectedEmployeeRows.map((row) => row.company.id))]
    const result = await payPayrollSelection.mutateAsync({
      companyIds,
      employees: selectedEmployeeRows.map((row) => ({
        companyId: row.company.id,
        employeeId: row.employee.id,
      })),
      asOf: preview?.asOf,
    })
    refreshPreview()
    navigate(`/payroll/history/${result.snapshot.id}`)
  }

  const calculateDisabled =
    calculatePreview.isPending ||
    (mode === 'company' && selectedCompanyIds.length === 0) ||
    (mode === 'person' &&
      (selectedUserIds.length === 0 || companyIdsForSelectedUsers.length === 0))

  return (
    <PageShell
      breadcrumbItems={[
        { label: 'Payroll', to: '/payroll' },
        { label: 'Calculate' },
      ]}
      icon={Calculator}
      title="Calculate Payroll"
      description="Calculate by company or by person, then review audit totals"
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        <WorkspaceToolbar
          filter={
            <div className="inline-flex h-11 items-center rounded-xl border border-gray-200 bg-gray-50 p-1 dark:border-gray-700 dark:bg-gray-900/40">
              <button
                type="button"
                onClick={() => switchMode('company')}
                className={`h-full rounded-lg px-3 text-sm font-medium ${
                  mode === 'company'
                    ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-800 dark:text-gray-100'
                    : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
                }`}
              >
                By company
              </button>
              <button
                type="button"
                onClick={() => switchMode('person')}
                className={`h-full rounded-lg px-3 text-sm font-medium ${
                  mode === 'person'
                    ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-800 dark:text-gray-100'
                    : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
                }`}
              >
                By person
              </button>
            </div>
          }
          actions={
            <>
              <MonthYearPicker
                year={year}
                month={month}
                onYearChange={setYear}
                onMonthChange={setMonth}
                hideLabels
              />
              <Button
                size="sm"
                className="h-11 rounded-xl"
                disabled={calculateDisabled}
                onClick={calculate}
              >
                <Calculator className="h-4 w-4" />
                <span className="ml-1.5">
                  Calculate
                </span>
              </Button>
              <Link to="/payroll/history">
                <Button variant="secondary" size="sm" className="h-11 rounded-xl">
                  <History className="h-4 w-4" />
                  <span className="ml-1.5">Paid history</span>
                </Button>
              </Link>
            </>
          }
        >
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <PayrollEntityMultiSelect
              label={mode === 'company' ? 'Companies' : 'People'}
              placeholder={mode === 'company' ? 'Select companies' : 'Select people'}
              options={mode === 'company' ? companyOptions : userOptions}
              selectedIds={mode === 'company' ? selectedCompanyIds : selectedUserIds}
              loading={mode === 'company' ? companiesLoading : usersLoading}
              onChange={mode === 'company' ? setSelectedCompanyIds : setSelectedUserIds}
              showLabel={false}
            />
          </div>
        </WorkspaceToolbar>

        <div className="text-xs text-gray-500 dark:text-gray-400">
          {mode === 'company'
            ? `${selectedCompanyIds.length} companies selected`
            : `${selectedUserIds.length} people selected · ${companyIdsForSelectedUsers.length} companies found`}
          {' '}for {periodLabel(year, month)}.
        </div>

        {((mode === 'company' && selectedCompanies.length > 0) ||
          (mode === 'person' && selectedUsers.length > 0)) &&
          !preview && (
          <section className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/10 dark:text-amber-300">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                Preview is runtime-only. Payment will need a saved snapshot, duplicate checks,
                salary slips, locks, and audit logs.
              </p>
            </div>
          </section>
        )}

        {preview && (
          <>
            <section className="grid gap-3 md:grid-cols-4 xl:grid-cols-8">
              <SummaryMetric label="Companies" value={preview.totals.companyCount} />
              <SummaryMetric label="Employees" value={preview.totals.employeeCount} />
              <SummaryMetric
                label="Ready"
                value={preview.totals.readyCount}
                tone="text-green-700 dark:text-green-400"
              />
              <SummaryMetric
                label="Pending"
                value={preview.totals.pendingCount}
                tone="text-amber-700 dark:text-amber-400"
              />
              <SummaryMetric label="Gross" value={fmt(preview.totals.gross)} />
              <SummaryMetric label="Deductions" value={fmt(preview.totals.deductions)} />
              <SummaryMetric label="Tax" value={fmt(preview.totals.tax)} />
              <SummaryMetric
                label="Net payable"
                value={fmt(preview.totals.netPay)}
                tone="text-green-700 dark:text-green-400"
              />
            </section>

            <div className="text-xs text-gray-500 dark:text-gray-400">
              Calculated {new Date(preview.asOf).toLocaleString()}
            </div>

            {preview.warnings.length > 0 && (
              <section className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/10 dark:text-amber-300">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <div className="font-medium">Preview warnings</div>
                    <ul className="mt-1 space-y-1">
                      {preview.warnings.map((warning) => (
                        <li key={warning}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>
            )}

            {showBreakdown && (
              mode === 'company' ? (
                <CompanyBreakdown companies={preview.companies} />
              ) : (
                <PersonBreakdown people={personRows} />
              )
            )}

            {employeeRows.length === 0 ? (
              <EmptyState
                icon={Users}
                title="No employees in this selection"
                description="Select companies with active members to calculate payroll."
              />
            ) : (
              <>
                {showEmployeeFilters && (
                  <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                    <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_180px_180px_auto_auto] lg:items-center">
                      <label className="relative block">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <input
                          value={employeeSearch}
                          onChange={(event) => setEmployeeSearch(event.target.value)}
                          placeholder="Search employees or companies"
                          className="h-10 w-full rounded-md border border-gray-300 bg-white pl-9 pr-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                        />
                      </label>
                      <select
                        value={companyFilter}
                        onChange={(event) => setCompanyFilter(event.target.value)}
                        className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                      >
                        <option value="all">All companies</option>
                        {preview.companies.map((company) => (
                          <option key={company.company.id} value={company.company.id}>
                            {company.company.name}
                          </option>
                        ))}
                      </select>
                      <select
                        value={statusFilter}
                        onChange={(event) =>
                          setStatusFilter(event.target.value as 'all' | 'ready' | 'blocked' | 'paid')
                        }
                        className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                      >
                        <option value="all">All statuses</option>
                        <option value="ready">Ready</option>
                        <option value="blocked">Pending</option>
                        <option value="paid">Paid</option>
                      </select>
                      <Button size="sm" variant="secondary" onClick={selectFilteredReady}>
                        Select ready
                      </Button>
                      <Button size="sm" variant="secondary" onClick={clearFiltered}>
                        Clear visible
                      </Button>
                    </div>
                    <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                      Showing {filteredEmployeeGroups.length} of {personRows.length} employees.
                    </div>
                  </section>
                )}
                <EmployeeRows
                  groups={filteredEmployeeGroups}
                  selectedEmployeeKeys={selectedEmployeeKeys}
                  onToggleGroup={toggleEmployeeGroup}
                  onOpenGroup={openEmployeeGroup}
                />
              </>
            )}
          </>
        )}

      </div>
      {preview && employeeRows.length > 0 && (
        <div className="sticky bottom-0 z-20 mt-6 border-t border-gray-200 bg-white/95 px-4 py-3 shadow-lg backdrop-blur dark:border-gray-700 dark:bg-gray-900/95">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {selectedEmployeeRows.length} employees selected
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Net payable: {fmt(selectedNetPay)}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={employeeRows.length === 0}
                onClick={exportCsv}
              >
                <Download className="mr-1.5 h-3.5 w-3.5" />
                Export selected
              </Button>
              <Button
                size="sm"
                disabled={selectedEmployeeRows.length === 0} loading={payPayrollSelection.isPending}
                onClick={() => void paySelected()}
              >
                <Banknote className="mr-1.5 h-3.5 w-3.5" />
                {payPayrollSelection.isPending ? 'Paying...' : 'Pay & send slips'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  )
}
